import { NetworkStatus } from '@apollo/client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIOptimisticNanoProgress from 'UIComponents/progress/UIOptimisticNanoProgress';
import UITable from 'UIComponents/table/UITable';
import EnrollmentsListErrorState from 'automation-ui-canvas/containers/enrollment-history/panel/enrollments-list/EnrollmentsListErrorState';
import NewEnrollmentsAlert from 'automation-ui-canvas/containers/enrollment-history/panel/enrollments-list/NewEnrollmentsAlert';
import SuccessfulEnrollmentRow from 'automation-ui-canvas/containers/enrollment-history/panel/enrollments-list/SuccessfulEnrollmentRow';
import SuccessfulEnrollmentsEmptyState from 'automation-ui-canvas/containers/enrollment-history/panel/enrollments-list/SuccessfulEnrollmentsEmptyState';
import { useEvictEnrollmentsList, useFilteredEnrollmentsList, useHasNewEnrollmentEvents } from 'automation-ui-canvas/containers/enrollment-history/panel/hooks/successfulEnrollmentsHooks';
import { ENROLLMENT_HISTORY_LIST_FIELDNAME } from 'automation-ui-canvas/containers/enrollment-history/panel/queries/successfulEnrollmentsQueries';
import { getEnrollmentHistoryListUrl } from 'automation-ui-canvas/utils/urls';
import { reportError } from 'automation-ui-core/errors/utils/reportError';
import { useApplicationHistoryFilters } from 'automation-ui-management/hooks/filterHooks';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import TrackableButton from 'automation-ui-usage-tracking/components/TrackableButton';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import { View } from 'foundations-components/transitional/components/View';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SuccessfulEnrollmentsContent = ({
  flow,
  isHybrid
}) => {
  var _paging$next;
  const trackEvent = useTrackEvent();
  const {
    search
  } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const history = useHistory();
  const {
    filters
  } = useApplicationHistoryFilters();
  const [seenEnrollmentIds, setSeenEnrollmentIds] = useState([]);
  const reachedActionIds = useMemo(() => {
    var _searchParams$get$spl, _searchParams$get;
    const stringActionIdsList = (_searchParams$get$spl = (_searchParams$get = searchParams.get('actions')) === null || _searchParams$get === void 0 ? void 0 : _searchParams$get.split(',')) !== null && _searchParams$get$spl !== void 0 ? _searchParams$get$spl : [];
    return stringActionIdsList.map(stringActionIds => parseInt(stringActionIds, 10));
  }, [searchParams]);
  const {
    data: {
      [ENROLLMENT_HISTORY_LIST_FIELDNAME]: enrollmentsResponse = {}
    } = {},
    loading: enrollmentListLoading,
    error,
    networkStatus,
    refetch,
    fetchMore
  } = useFilteredEnrollmentsList({
    flowId: flow.flowId,
    objectTypeId: flow.objectTypeId,
    hybrid: !!isHybrid,
    skip: !flow.objectTypeId,
    reachedActionIds,
    seenEnrollmentIds
  });
  const handleClearFilters = () => {
    history.replace({
      pathname: getEnrollmentHistoryListUrl({
        flowId: flow.flowId
      }),
      search: searchParams.get('showVersions') ? '?showVersions=true' : ''
    });
    trackEvent(Trackables.PERFORMANCE_HISTORY_FILTERS_CLEAR);
  };
  const evictEnrollmentsList = useEvictEnrollmentsList();
  const {
    results: enrollmentsList = [],
    paging
  } = enrollmentsResponse;
  const hasMore = !!(paging !== null && paging !== void 0 && paging.next);
  const offset = paging === null || paging === void 0 || (_paging$next = paging.next) === null || _paging$next === void 0 ? void 0 : _paging$next.after;
  const {
    hasNewEnrollments,
    resetPolling
  } = useHasNewEnrollmentEvents({
    flowId: flow.flowId,
    skip: !flow.objectTypeId
  });
  const fetchNewEnrollments = () => {
    evictEnrollmentsList();
    resetPolling();
    handleClearFilters();
  };
  useEffect(() => {
    if (reachedActionIds.length > 0 && enrollmentsList.length !== seenEnrollmentIds.length) {
      setSeenEnrollmentIds(enrollmentsList.map(enrollment => enrollment.enrollmentId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentsList]);
  const loading = enrollmentListLoading ||
  // This is to avoid weird flickering in the content section while revision groups are loading
  searchParams.get('showVersions') === 'true' && !searchParams.get('version');
  if (loading && networkStatus !== NetworkStatus.fetchMore) {
    return /*#__PURE__*/_jsx(UIOptimisticNanoProgress, {
      value: 10
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(EnrollmentsListErrorState, {
      onTryAgain: () => {
        refetch().catch(err => {
          reportError({
            error: err
          });
        });
      }
    });
  }
  if (enrollmentsList.length === 0) {
    return /*#__PURE__*/_jsx(SuccessfulEnrollmentsEmptyState, {
      flowObjectTypeId: flow.objectTypeId,
      isFilterApplied: filters.hasFiltersApplied(),
      onClearFilters: handleClearFilters
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [hasNewEnrollments && /*#__PURE__*/_jsx(NewEnrollmentsAlert, {
      fetchNewEnrollments: fetchNewEnrollments,
      flowObjectTypeId: flow.objectTypeId
    }), /*#__PURE__*/_jsxs(UITable, {
      children: [/*#__PURE__*/_jsxs("colgroup", {
        children: [/*#__PURE__*/_jsx("col", {
          style: {
            width: '70%'
          }
        }), /*#__PURE__*/_jsx("col", {
          style: {
            width: '30%'
          }
        })]
      }), /*#__PURE__*/_jsx("tbody", {
        children: enrollmentsList.map(enrollmentInfo => /*#__PURE__*/_jsx(SuccessfulEnrollmentRow, {
          enrollmentInfo: enrollmentInfo,
          flow: flow
        }, enrollmentInfo.enrollmentId))
      })]
    }), hasMore && /*#__PURE__*/_jsx(View, {
      textAlign: "center",
      marginBlockEnd: "400",
      children: /*#__PURE__*/_jsx(TrackableButton, {
        loading: networkStatus === NetworkStatus.fetchMore,
        onClick: () => {
          fetchMore({
            variables: {
              offset
            }
          }).catch(err => {
            reportError({
              error: err
            });
          });
        },
        trackableKey: Trackables.HISTORY_LOAD_MORE,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation.enrollmentHistory.panel.enrollment-list.table.loadMoreEnrollments"
        })
      })
    })]
  });
};
export default SuccessfulEnrollmentsContent;